import { useState, useRef, useEffect, useCallback } from "react";
import { Play, Pause, Maximize, Volume2, VolumeX, Loader2 } from "lucide-react";

interface MuxPlayerProps {
  playbackId: string;
  poster?: boolean;
  autoPlay?: boolean;
  className?: string;
  aspectRatio?: string;
  onPlay?: () => void;
}

const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const MuxPlayer = ({ playbackId, poster = true, autoPlay = false, className = "", aspectRatio = "aspect-video", onPlay }: MuxPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(autoPlay);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const thumbnailUrl = `https://image.mux.com/${playbackId}/thumbnail.jpg?width=640&height=360&fit_mode=smartcrop`;
  const animatedGif = `https://image.mux.com/${playbackId}/animated.gif?width=320&fps=15&end=4`;
  const hlsUrl = `https://stream.mux.com/${playbackId}.m3u8`;

  const [showGif, setShowGif] = useState(false);
  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  useEffect(() => {
    if (!videoRef.current) return;
    const v = videoRef.current;

    const onCanPlay = () => setLoading(false);
    const onError = () => { setError(true); setLoading(false); };
    const onPlayEvent = () => { setPlaying(true); onPlay?.(); };
    const onPause = () => setPlaying(false);
    const onDurationChange = () => { if (v.duration && isFinite(v.duration)) setDuration(v.duration); };
    const onTimeUpdate = () => setCurrentTime(v.currentTime);

    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("error", onError);
    v.addEventListener("play", onPlayEvent);
    v.addEventListener("pause", onPause);
    v.addEventListener("durationchange", onDurationChange);
    v.addEventListener("timeupdate", onTimeUpdate);

    if (v.canPlayType("application/vnd.apple.mpegurl")) {
      v.src = hlsUrl;
    } else {
      import("hls.js").then(({ default: Hls }) => {
        if (Hls.isSupported()) {
          const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
          hls.loadSource(hlsUrl);
          hls.attachMedia(v);
        }
      }).catch(() => { v.src = hlsUrl; });
    }

    return () => {
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("error", onError);
      v.removeEventListener("play", onPlayEvent);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("durationchange", onDurationChange);
      v.removeEventListener("timeupdate", onTimeUpdate);
    };
  }, [hlsUrl]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) videoRef.current.pause();
    else videoRef.current.play();
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(!muted);
  };

  const toggleFullscreen = () => { videoRef.current?.requestFullscreen?.(); };

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    videoRef.current.currentTime = pct * duration;
  }, [duration]);

  if (error) {
    return (
      <div className={`${aspectRatio} bg-secondary/30 rounded-xl flex items-center justify-center ${className}`}>
        <p className="text-xs text-muted-foreground">Video unavailable</p>
      </div>
    );
  }

  return (
    <div
      className={`${aspectRatio} relative rounded-xl overflow-hidden bg-background group ${className}`}
      onMouseEnter={() => setShowGif(true)}
      onMouseLeave={() => setShowGif(false)}
    >
      {/* Lazy thumbnail */}
      {!playing && poster && (
        <img
          src={showGif ? animatedGif : thumbnailUrl}
          alt="Video thumbnail"
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
          loading="lazy"
        />
      )}

      <video
        ref={videoRef}
        autoPlay={autoPlay}
        muted={muted}
        playsInline
        className={`w-full h-full object-cover ${!playing && poster ? "opacity-0" : "opacity-100"} transition-opacity`}
      />

      {/* Duration badge (when not playing) */}
      {!playing && !loading && duration > 0 && (
        <div className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-background/80 backdrop-blur-sm text-[10px] font-mono text-foreground">
          {formatTime(duration)}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/50">
          <Loader2 className="w-6 h-6 text-sovereign-gold animate-spin" />
        </div>
      )}

      {/* Controls overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Progress bar */}
        <div
          className="absolute bottom-10 left-3 right-3 h-1.5 bg-muted/40 rounded-full cursor-pointer group/progress"
          onClick={handleProgressClick}
        >
          <div
            className="h-full bg-sovereign-gold rounded-full relative transition-[width] duration-100"
            style={{ width: `${progressPct}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-sovereign-gold opacity-0 group-hover/progress:opacity-100 transition-opacity shadow" />
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={togglePlay} className="p-1.5 rounded-full bg-background/60 backdrop-blur-sm text-foreground hover:bg-background/80 transition-colors">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <button onClick={toggleMute} className="p-1.5 rounded-full bg-background/60 backdrop-blur-sm text-foreground hover:bg-background/80 transition-colors">
              {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            </button>
            {duration > 0 && (
              <span className="text-[10px] font-mono text-foreground/80">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            )}
          </div>
          <button onClick={toggleFullscreen} className="p-1.5 rounded-full bg-background/60 backdrop-blur-sm text-foreground hover:bg-background/80 transition-colors">
            <Maximize className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Play button center */}
      {!playing && !loading && (
        <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-sovereign-gold/90 flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
            <Play className="w-6 h-6 text-background ml-0.5" />
          </div>
        </button>
      )}
    </div>
  );
};

export default MuxPlayer;
